import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Check, ChevronDown, Loader2, ShieldCheck, UserCog, Wallet } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { api, errMsg } from "../lib/api";
import { useAuth, homePathFor } from "../context/AuthContext";

/**
 * Phase 174 — Role switcher (one login identity, a linked account per role).
 *
 * Shows the roles the signed-in person holds; picking another one calls `POST /t/{code}/auth/switch-role`,
 * swaps the stored session for the chosen role account and reloads straight into that role's portal — no
 * password, no sign-out. Renders nothing for single-role staff.
 *
 * `variant="onPrimary"` renders the trigger for the collector's dark (primary) header.
 */
const ROLE_ICON = { municipal_admin: ShieldCheck, supervisor: UserCog, tax_collector: Wallet };
const STAFF = ["municipal_admin", "supervisor", "tax_collector"];

export const RoleSwitcher = ({ code, variant = "default", compact = false }) => {
  const { auth, login, setRoles } = useAuth();
  const me = auth?.user;
  const [busy, setBusy] = useState(null);

  // Fresh role list on mount (a role the Super Admin just added appears without re-login).
  useEffect(() => {
    if (!code || !me?.id || !STAFF.includes(me.role)) return undefined;
    let alive = true;
    api.get(`/t/${code}/auth/me`)
      .then((r) => { if (alive && Array.isArray(r.data?.roles)) setRoles(r.data.roles); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, me?.id]);

  const active = useMemo(() => (auth?.roles || []).filter((r) => r.is_active !== false), [auth?.roles]);
  const current = active.find((r) => r.is_current) || active.find((r) => r.user_id === me?.id) || null;

  if (!code || !me || !STAFF.includes(me.role) || active.length < 2) return null;

  const switchTo = async (role) => {
    if (!role || role.is_current || role.user_id === me.id || busy) return;
    setBusy(role.role);
    try {
      const r = await api.post(`/t/${code}/auth/switch-role`, { role: role.role });
      if (!r.data?.token) { setBusy(null); return; }
      const next = { token: r.data.token, user: r.data.user, municipality: r.data.municipality || auth.municipality, roles: r.data.roles || [] };
      login(next);
      toast.success(`Switched to ${role.label}`, { description: "Opening your " + role.label + " portal…" });
      // Full reload: every cached per-user state (modules, territory, offline pack, bell) starts clean for the new role.
      window.location.assign(homePathFor(next));
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  const onPrimary = variant === "onPrimary";
  const triggerCls = onPrimary
    ? "text-primary-foreground hover:bg-primary-foreground/10 focus-visible:ring-primary-foreground/70"
    : "border border-input bg-background hover:bg-muted focus-visible:ring-ring";
  const CurIcon = ROLE_ICON[current?.role] || ArrowLeftRight;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button"
          className={`inline-flex items-center gap-1.5 rounded-full ${compact ? "h-9 w-9 justify-center" : "h-8 pl-2.5 pr-2"} text-xs font-medium focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60 ${triggerCls}`}
          title={`Switch role — you hold ${active.length} roles`} aria-label="Switch role" disabled={!!busy}
          data-testid="role-switcher-trigger">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
          {!compact && (
            <>
              <span className="hidden sm:inline max-w-[9rem] truncate" data-testid="role-switcher-current">{current?.label || me.role.replace(/_/g, " ")}</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="role-switcher-menu">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Switch role · signed in as <span className="font-medium text-foreground">{me.name}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {active.map((r) => {
          const Icon = ROLE_ICON[r.role] || CurIcon;
          const isCur = r.is_current || r.user_id === me.id;
          return (
            <DropdownMenuItem key={r.user_id} onSelect={() => switchTo(r)} disabled={isCur || !!busy}
              className="gap-2.5 py-2 cursor-pointer" data-testid={`role-switcher-option-${r.role}`}>
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{r.label}</span>
                <span className="block text-[11px] text-muted-foreground truncate">
                  {isCur ? "Current role" : r.employee_id ? `Employee ${r.employee_id}` : "Open this role's portal"}
                </span>
              </span>
              {isCur && <Check className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />}
              {busy === r.role && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-[11px] text-muted-foreground leading-snug">
          Each role keeps its own territory, targets and collections. Your password and photo are shared.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
